import { useState, useEffect, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Calendar, RefreshCw, Trash2, ExternalLink, ImageIcon, Video, Music, Filter, Clock, User, RotateCcw, Zap, XCircle, Activity, ChevronLeft, ChevronRight, CreditCard, Plus, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { checkTaskStatus } from "@/lib/task-checker";

interface GenerationLog {
    id: string;
    user_id: string;
    service_type: "photo" | "video" | "music" | "video_motion";
    model: string;
    prompt: string;
    display_prompt?: string | null;
    is_professional?: boolean;
    credits_used: number;
    result_url: string | null;
    task_id?: string | null;
    status: "pending" | "processing" | "completed" | "failed";
    created_at: string;
    refunded_at?: string | null;
    refunded_credits?: number | null;
    error_message?: string | null;
    profiles?: {
        email: string;
        display_name: string | null;
    };
}

interface UserGenerationLogsProps {
    userId?: string | null;
}

type ConfirmAction = "markFailed" | "refund";

interface CreditOrder {
    id: string;
    user_id: string;
    credits_amount: number;
    bonus_credits: number;
    price_thb: number;
    status: string;
    created_at: string;
    completed_at: string | null;
}

const ITEMS_PER_PAGE = 30;

const UserGenerationLogs = ({ userId }: UserGenerationLogsProps) => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [logs, setLogs] = useState<GenerationLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState<string>("all");
    const [filterStatus, setFilterStatus] = useState<string>("all");
    const [checkingStatus, setCheckingStatus] = useState<string | null>(null);
    const [recheckingAll, setRecheckingAll] = useState(false);
    const [refundingLogId, setRefundingLogId] = useState<string | null>(null);
    const [confirmState, setConfirmState] = useState<{
        action: ConfirmAction | null;
        log: GenerationLog | null;
    }>({ action: null, log: null });

    const [singleUserProfile, setSingleUserProfile] = useState<{ email: string; display_name: string | null } | null>(null);
    const [userCredits, setUserCredits] = useState<number | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);

    // Credit Orders
    const [creditOrders, setCreditOrders] = useState<CreditOrder[]>([]);
    const [showCreditHistory, setShowCreditHistory] = useState(false);

    // Image Preview Popup
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

    // Recheck a single job - auto refund if failed
    const handleRecheckJob = async (logId: string, alreadyFailed?: boolean) => {
        setCheckingStatus(logId);
        try {
            // If already failed, just do refund directly
            if (alreadyFailed) {
                try {
                    const { data: refundResult, error: refundError } = await supabase.rpc(
                        "refund_credits_for_failed_generation" as any,
                        { p_log_id: logId }
                    );

                    if (refundError) {
                        toast({
                            title: "Refund Error",
                            description: refundError.message,
                            variant: "destructive",
                        });
                    } else if (refundResult?.success) {
                        toast({
                            title: "✅ Refunded",
                            description: `${refundResult.refunded_credits} credits refunded to user.`,
                        });
                    } else if (refundResult?.error === "Already refunded") {
                        toast({
                            title: "Already Refunded",
                            description: "Credits were already refunded for this job.",
                        });
                    } else {
                        toast({
                            title: "Refund Issue",
                            description: refundResult?.error || "Could not refund",
                            variant: "destructive",
                        });
                    }
                } catch (refundErr: any) {
                    toast({
                        title: "Refund Error",
                        description: refundErr.message,
                        variant: "destructive",
                    });
                }
                fetchLogs();
                setCheckingStatus(null);
                return;
            }

            const result = await checkTaskStatus(logId);

            if (result.success && result.status === "completed" && result.url) {
                toast({
                    title: "✅ Completed!",
                    description: "Job has been completed successfully",
                });
                fetchLogs(); // Refresh the list
            } else if (result.status === "processing") {
                toast({
                    title: "⏳ Still Processing",
                    description: "Job is still being processed",
                });
            } else if (result.status === "failed") {
                // Auto refund when job is failed
                try {
                    const { data: refundResult, error: refundError } = await supabase.rpc(
                        "refund_credits_for_failed_generation" as any,
                        { p_log_id: logId }
                    );

                    if (refundError) {
                        console.error("Auto-refund error:", refundError);
                        toast({
                            title: "❌ Failed",
                            description: `Job failed. Refund error: ${refundError.message}`,
                            variant: "destructive",
                        });
                    } else if (refundResult?.success) {
                        toast({
                            title: "❌ Failed - Auto Refunded",
                            description: `Job failed. ${refundResult.refunded_credits} credits refunded to user.`,
                            variant: "destructive",
                        });
                    } else if (refundResult?.error === "Already refunded") {
                        toast({
                            title: "❌ Failed",
                            description: "Job failed. Credits were already refunded.",
                            variant: "destructive",
                        });
                    } else {
                        toast({
                            title: "❌ Failed",
                            description: result.message || "Job failed",
                            variant: "destructive",
                        });
                    }
                } catch (refundErr: any) {
                    console.error("Auto-refund exception:", refundErr);
                    toast({
                        title: "❌ Failed",
                        description: `Job failed. Could not auto-refund: ${refundErr.message}`,
                        variant: "destructive",
                    });
                }
                fetchLogs(); // Refresh to show updated status
            } else {
                toast({
                    title: "ℹ️ Status",
                    description: result.message || `Status: ${result.status}`,
                });
            }
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to check status",
                variant: "destructive",
            });
        } finally {
            setCheckingStatus(null);
        }
    };

    // Recover a refunded job - check KIE and re-deduct credits if successful
    const handleRecoverRefundedJob = async (log: GenerationLog) => {
        setCheckingStatus(log.id);
        try {
            // Get taskId from result_url (might be stored before refund)
            const taskId = log.result_url;

            if (!taskId || taskId.startsWith("http")) {
                // If already has URL, just mark as completed
                if (taskId?.startsWith("http")) {
                    // Re-deduct credits
                    const { error: deductError } = await supabase.rpc(
                        "admin_deduct_credits" as any,
                        {
                            p_user_id: log.user_id,
                            p_credits: log.credits_used
                        }
                    );

                    if (deductError) {
                        toast({
                            title: "Credit Deduction Failed",
                            description: deductError.message,
                            variant: "destructive",
                        });
                        return;
                    }

                    // Update log status
                    await supabase
                        .from("generation_logs")
                        .update({
                            status: "completed",
                            refunded_at: null,
                            refunded_credits: null,
                        })
                        .eq("id", log.id);

                    toast({
                        title: "✅ Recovered!",
                        description: `Job recovered. ${log.credits_used} credits deducted from user.`,
                    });
                    fetchLogs();
                } else {
                    toast({
                        title: "No Task ID",
                        description: "No taskId found to check KIE status",
                        variant: "destructive",
                    });
                }
                return;
            }

            // Poll KIE for result
            const result = await checkTaskStatus(log.id);

            if (result.success && result.status === "completed" && result.url) {
                // Found the video! Re-deduct credits from user
                const { error: deductError } = await supabase.rpc(
                    "admin_deduct_credits" as any,
                    {
                        p_user_id: log.user_id,
                        p_credits: log.credits_used
                    }
                );

                if (deductError) {
                    toast({
                        title: "Credit Deduction Failed",
                        description: `Video found but failed to deduct credits: ${deductError.message}`,
                        variant: "destructive",
                    });
                    return;
                }

                // Clear refund fields
                await supabase
                    .from("generation_logs")
                    .update({
                        refunded_at: null,
                        refunded_credits: null,
                    })
                    .eq("id", log.id);

                toast({
                    title: "✅ Recovered!",
                    description: `Video found! ${log.credits_used} credits deducted from user.`,
                });
                fetchLogs();
            } else if (result.status === "processing") {
                toast({
                    title: "⏳ Still Processing",
                    description: "KIE is still processing this job. Try again later.",
                });
            } else {
                toast({
                    title: "❌ Not Found",
                    description: result.message || "Video not found on KIE. Keeping refund.",
                });
            }
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to recover job",
                variant: "destructive",
            });
        } finally {
            setCheckingStatus(null);
        }
    };

    // Recheck all stuck jobs via Edge Function
    const handleRecheckAllStuckJobs = async () => {
        setRecheckingAll(true);
        try {
            const response = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/recheck-stuck-jobs`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();

            if (data.ok) {
                toast({
                    title: "✅ Recheck Complete",
                    description: `Processed ${data.processed} stuck jobs`,
                });
                fetchLogs(); // Refresh the list
            } else {
                throw new Error(data.error || "Failed to recheck");
            }
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to recheck stuck jobs",
                variant: "destructive",
            });
        } finally {
            setRecheckingAll(false);
        }
    };

    // Mark job as failed and refund credits
    const executeMarkFailed = async (logId: string) => {
        if (checkingStatus === logId) return;
        setCheckingStatus(logId);
        try {
            // Refund credits FIRST (while status is still processing/pending)
            const { data: refundResult, error: refundError } = await supabase.rpc(
                "refund_credits_for_failed_generation" as any,
                { p_log_id: logId }
            );

            if (refundError) {
                console.error("Refund error:", refundError);
                // If refund fails, still update status but note the issue
            }

            // If refund didn't happen (already refunded), just notify
            if (refundResult?.error === "Already refunded") {
                toast({
                    title: "Already Refunded",
                    description: "Credits were already refunded for this job",
                });
            } else if (!refundResult?.success) {
                // If refund didn't happen, still update status
                const { error: updateError } = await supabase
                    .from("generation_logs")
                    .update({
                        status: "failed",
                        completed_at: new Date().toISOString(),
                    })
                    .eq("id", logId);

                if (updateError) throw updateError;
            }

            toast({
                title: "✅ Job Marked as Failed",
                description: refundResult?.success
                    ? `${refundResult.refunded_credits} credits have been refunded to user`
                    : "Job marked as failed (already refunded or was already failed)",
            });

            fetchLogs();
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to mark job as failed",
                variant: "destructive",
            });
        } finally {
            setCheckingStatus(null);
        }
    };

    const handleManualRefund = async (log: GenerationLog) => {
        if (!log.user_id) {
            toast({
                title: "Error",
                description: "Missing user id for refund",
                variant: "destructive",
            });
            return;
        }

        if (!log.credits_used || log.credits_used <= 0) {
            toast({
                title: "Error",
                description: "No credits to refund for this job",
                variant: "destructive",
            });
            return;
        }

        // Confirmation is handled by AlertDialog, no need for native confirm()

        setRefundingLogId(log.id);
        try {
            const { data, error: rpcError } = await supabase.rpc(
                "refund_credits_for_failed_generation" as any,
                { p_log_id: log.id }
            );

            if (rpcError) {
                throw rpcError;
            }

            const result = data as { success: boolean; refunded_credits?: number; error?: string };

            if (!result?.success) {
                if (result?.error === "Already refunded") {
                    toast({
                        title: "Already Refunded",
                        description: "Credits were already refunded for this job",
                    });
                    fetchLogs();
                    return;
                }
                throw new Error(result?.error || "Refund did not update any user credits");
            }

            toast({
                title: "✅ Credits Refunded",
                description: `${result.refunded_credits ?? log.credits_used} credits have been added back to the user`,
            });

            fetchLogs();
        } catch (err: any) {
            console.error('Refund error:', err);
            toast({
                title: "Error",
                description: err.message || "Failed to refund credits",
                variant: "destructive",
            });
        } finally {
            setRefundingLogId(null);
        }
    };

    const openConfirm = (action: ConfirmAction, log: GenerationLog, event?: MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();
        event?.stopPropagation();
        setConfirmState({ action, log });
    };

    const closeConfirm = () => {
        setConfirmState({ action: null, log: null });
    };

    const handleConfirmAction = async () => {
        if (!confirmState.action || !confirmState.log) return;
        const log = confirmState.log;
        const action = confirmState.action;

        closeConfirm();

        if (action === "markFailed") {
            await executeMarkFailed(log.id);
        } else {
            await handleManualRefund(log);
        }
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            let userProfile: { email: string; display_name: string | null } | null = null;

            // 1. Fetch user profile first if userId is provided
            if (userId) {
                const { data: profileData } = await supabase
                    .from("profiles")
                    .select("email, display_name, credits")
                    .eq("user_id", userId)
                    .single();

                if (profileData) {
                    userProfile = { email: profileData.email, display_name: profileData.display_name };
                    setSingleUserProfile(userProfile);
                    setUserCredits(profileData.credits || 0);
                }
            }

            // 2. Fetch Logs
            let query = supabase
                .from("generation_logs")
                .select("*")
                .order("created_at", { ascending: false });

            if (userId) {
                query = query.eq("user_id", userId);
            }

            const { data: logsData, error: logsError } = await query.limit(100);
            if (logsError) throw logsError;

            const baseLogs = logsData as any[];

            // 3. Attach profiles to logs
            if (!userId && baseLogs.length > 0) {
                // All Users view - fetch profiles for all unique user IDs
                const uniqueUserIds = Array.from(new Set(baseLogs.map(l => l.user_id)));

                const { data: profilesData, error: profilesError } = await supabase
                    .from("profiles")
                    .select("user_id, email, display_name")
                    .in("user_id", uniqueUserIds);

                if (!profilesError && profilesData) {
                    const profileMap = new Map(profilesData.map(p => [p.user_id, p]));
                    const logsWithProfiles = baseLogs.map(log => ({
                        ...log,
                        profiles: profileMap.get(log.user_id)
                    }));
                    setLogs(logsWithProfiles as GenerationLog[]);
                } else {
                    setLogs(baseLogs as GenerationLog[]);
                }
            } else if (userId && baseLogs.length > 0) {
                // Single user view - use the profile we fetched earlier
                const logsWithProfile = baseLogs.map(log => ({
                    ...log,
                    profiles: userProfile
                }));
                setLogs(logsWithProfile as GenerationLog[]);
            } else {
                setLogs(baseLogs as GenerationLog[]);
            }
        } catch (error: any) {
            console.error("Error fetching generation logs:", error);
            toast({
                title: "Error",
                description: "Failed to fetch generation logs",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch credit orders for user
    const fetchCreditOrders = async () => {
        if (!userId) {
            setCreditOrders([]);
            return;
        }

        try {
            const { data, error } = await (supabase as any)
                .from("credit_orders")
                .select("*")
                .eq("user_id", userId)
                .order("created_at", { ascending: false });

            if (!error && data) {
                setCreditOrders(data);
            }
        } catch (err) {
            console.error("Error fetching credit orders:", err);
        }
    };

    useEffect(() => {
        // Reset state when viewing a different user to prevent showing stale data
        setSingleUserProfile(null);
        setUserCredits(null);
        setCreditOrders([]);
        setLogs([]);
        setCurrentPage(1);
        fetchLogs();
        fetchCreditOrders();
    }, [userId]);

    // Update logs when singleUserProfile changes (for the single user case)
    useEffect(() => {
        if (userId && singleUserProfile && logs.length > 0 && !logs[0].profiles) {
            setLogs(currentLogs => currentLogs.map(log => ({
                ...log,
                profiles: singleUserProfile
            })));
        }
    }, [singleUserProfile, userId]);

    const filteredLogs = logs.filter(log => {
        const matchesSearch =
            log.prompt.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (log.profiles?.email?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
            (log.profiles?.display_name?.toLowerCase() || "").includes(searchQuery.toLowerCase());

        const matchesType = filterType === "all" || log.service_type === filterType;
        const matchesStatus = filterStatus === "all" || log.status === filterStatus;

        return matchesSearch && matchesType && matchesStatus;
    });

    // Pagination
    const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
    const paginatedLogs = filteredLogs.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterType, filterStatus]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString("th-TH", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "completed":
                return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Completed</Badge>;
            case "processing":
                return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 animate-pulse">Processing</Badge>;
            case "pending":
                return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Pending</Badge>;
            case "failed":
                return <Badge variant="destructive">Failed</Badge>;
            default:
                return null;
        }
    };

    const getServiceIcon = (type: string) => {
        switch (type) {
            case "photo": return <ImageIcon className="h-4 w-4" />;
            case "video": return <Video className="h-4 w-4" />;
            case "video_motion": return <Video className="h-4 w-4" />;
            case "music": return <Music className="h-4 w-4" />;
            default: return null;
        }
    };

    return (
        <Card variant="glass">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle>History {userId ? '(User Specific)' : '(All Users)'}</CardTitle>
                    {userId && singleUserProfile ? (
                        <div className="flex flex-col gap-1 mt-2">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-foreground">
                                    {singleUserProfile.display_name || 'Anonymous'}
                                </span>
                                <span className="text-xs text-muted-foreground">({singleUserProfile.email})</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowCreditHistory(true)}
                                    className="text-xs font-bold px-2 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                                >
                                    💎 {userCredits !== null ? userCredits : '...'} CREDITS
                                    <span className="ml-1 opacity-60">▼</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <CardDescription>View generation history and results</CardDescription>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRecheckAllStuckJobs}
                        disabled={recheckingAll}
                        className="bg-amber-500/10 border-amber-500/30 text-amber-500 hover:bg-amber-500/20"
                    >
                        {recheckingAll ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Zap className="h-4 w-4 mr-2" />
                        )}
                        Recheck All Stuck
                    </Button>
                    <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search prompt, email or name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 h-10 bg-white/5 border border-white/10 rounded-lg outline-none focus:ring-1 focus:ring-primary/30"
                        />
                    </div>
                </div>

                {/* Type Filter */}
                <div className="mb-3">
                    <div className="text-xs text-muted-foreground mb-2 font-medium">Filter by Type:</div>
                    <div className="flex gap-2 flex-wrap">
                        {["all", "photo", "video", "video_motion", "music"].map((type) => (
                            <Button
                                key={type}
                                variant={filterType === type ? "default" : "outline"}
                                size="sm"
                                onClick={() => setFilterType(type)}
                                className="capitalize"
                            >
                                {type === "all" ? <Filter className="h-3 w-3 mr-1" /> : getServiceIcon(type)}
                                <span className="ml-1">{type}</span>
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Status Filter */}
                <div className="mb-6">
                    <div className="text-xs text-muted-foreground mb-2 font-medium">Filter by Status:</div>
                    <div className="flex gap-2 flex-wrap">
                        {["all", "completed", "failed", "processing", "pending"].map((status) => (
                            <Button
                                key={status}
                                variant={filterStatus === status ? "default" : "outline"}
                                size="sm"
                                onClick={() => setFilterStatus(status)}
                                className="capitalize"
                            >
                                {status === "all" && <Filter className="h-3 w-3 mr-1" />}
                                {status === "completed" && "✓"}
                                {status === "failed" && "✗"}
                                {status === "processing" && "⟳"}
                                {status === "pending" && "⏱"}
                                <span className="ml-1">{status}</span>
                            </Button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        No generation history found
                    </div>
                ) : (
                    <div className="space-y-4">
                        {paginatedLogs.map((log) => (
                            <div key={log.id} className="p-4 rounded-xl bg-muted/30 border border-white/5 hover:bg-muted/50 transition-colors">
                                <div className="flex flex-col md:flex-row gap-4">
                                    {/* Result Thumbnail / Preview */}
                                    <div className={`w-full md:w-48 ${(log.service_type === 'video' || log.service_type === 'video_motion') ? 'aspect-video md:w-64' : 'aspect-video'} bg-black/20 rounded-lg overflow-hidden shrink-0 border border-white/5`}>
                                        {log.result_url && log.status === 'completed' ? (
                                            log.service_type === "photo" ? (
                                                <img src={log.result_url} alt="Result" className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform" onClick={() => setPreviewImageUrl(log.result_url!)} />
                                            ) : log.service_type === "music" ? (() => {
                                                let coverImage: string | null = null;
                                                let tracks: { audio: string; title?: string }[] = [];
                                                try {
                                                    const parsed = JSON.parse(log.result_url || "");
                                                    if (parsed.tracks) {
                                                        tracks = parsed.tracks;
                                                        if (parsed.tracks[0]?.image) coverImage = parsed.tracks[0].image;
                                                    }
                                                } catch {
                                                    tracks = (log.result_url || "").split(',').filter(u => u.startsWith('http')).map(u => ({ audio: u }));
                                                }
                                                const getMusicUrl = (url: string) => (url.includes('musicfile.kie.ai') && !url.includes('.mp3')) ? url + '.mp3' : url;

                                                return (
                                                    <div className="w-full h-full flex flex-col bg-muted/10">
                                                        <div className="flex-1 relative overflow-hidden">
                                                            {coverImage ? (
                                                                <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center"><Music className="h-8 w-8 text-primary/40" /></div>
                                                            )}
                                                        </div>
                                                        <div className="p-2 bg-black/40">
                                                            {tracks.slice(0, 1).map((track, idx) => (
                                                                <audio key={idx} src={getMusicUrl(track.audio)} controls className="w-full h-6" />
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })() : (log.service_type === "video" || log.service_type === "video_motion") ? (() => {
                                                let videoUrl = log.result_url;
                                                if (videoUrl?.startsWith('{')) {
                                                    try {
                                                        const parsed = JSON.parse(videoUrl);
                                                        videoUrl = parsed.resultUrls?.[0] || parsed.url || videoUrl;
                                                    } catch { }
                                                } else if (videoUrl?.includes(',')) {
                                                    videoUrl = videoUrl.split(',')[0];
                                                }
                                                return (
                                                    <video src={videoUrl || ""} className="w-full h-full object-contain bg-black" controls preload="metadata" playsInline />
                                                );
                                            })() : null
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground/30">
                                                {log.status === 'failed' ? <Trash2 className="h-8 w-8 text-destructive/50" /> : <Clock className="h-8 w-8 animate-pulse" />}
                                                <span className="text-[10px] font-bold uppercase">{log.status}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Badge variant="outline" className="text-[10px] gap-1 py-0 px-2 h-5">
                                                    {getServiceIcon(log.service_type)}
                                                    {log.service_type.toUpperCase()}
                                                </Badge>
                                                <span className="text-[10px] bg-white/5 px-2 py-0.5 rounded text-muted-foreground font-mono">{log.model}</span>
                                                {getStatusBadge(log.status)}
                                                {log.is_professional && <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px] h-5">PRO</Badge>}
                                            </div>
                                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground whitespace-nowrap">
                                                <Clock className="h-3 w-3" />
                                                {formatDate(log.created_at)}
                                            </div>
                                        </div>

                                        <p className="text-sm line-clamp-2 mb-3 text-foreground/80 italic">"{log.prompt}"</p>

                                        {/* Error Message for Failed Jobs */}
                                        {log.status === 'failed' && log.error_message && (
                                            <div className="flex items-start gap-2 mb-3 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                                                <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                                                <p className="text-xs text-red-400">{log.error_message}</p>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between mt-auto pt-2 border-t border-white/5">
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                                        <User className="h-3 w-3 text-primary" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[11px] font-medium leading-none">{log.profiles?.display_name || 'User'}</span>
                                                        <span className="text-[9px] text-muted-foreground">{log.profiles?.email}</span>
                                                    </div>
                                                </div>
                                                <div className="px-2 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold">
                                                    {log.refunded_at
                                                        ? `REFUNDED +${log.refunded_credits ?? log.credits_used}`
                                                        : `${log.credits_used} CREDITS`}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 relative z-10 pointer-events-auto">
                                                {/* Recheck button for processing/pending/failed jobs (not already refunded) */}
                                                {(log.status === 'processing' || log.status === 'pending' || (log.status === 'failed' && !log.refunded_at)) && (
                                                    <>
                                                        {/* Show Recheck for all pending/processing/failed jobs - auto refund if failed */}
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className={`h-8 px-3 text-xs gap-1.5 ${log.status === 'failed'
                                                                ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20'
                                                                : 'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20'}`}
                                                            onClick={() => handleRecheckJob(log.id, log.status === 'failed')}
                                                            disabled={checkingStatus === log.id}
                                                        >
                                                            {checkingStatus === log.id ? (
                                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                            ) : (
                                                                <RotateCcw className="h-3.5 w-3.5" />
                                                            )}
                                                            {log.status === 'failed' ? 'Recheck & Refund' : 'Recheck'}
                                                        </Button>
                                                        {/* Always show Mark Failed for stuck jobs (not for already failed) */}
                                                        {log.status !== 'failed' && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 px-3 text-xs gap-1.5 bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
                                                                type="button"
                                                                onClick={(event) => openConfirm("markFailed", log, event)}
                                                                disabled={checkingStatus === log.id}
                                                            >
                                                                <XCircle className="h-3.5 w-3.5" />
                                                                Mark Failed
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                                {log.result_url && log.status === 'completed' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 px-3 text-xs gap-1.5 bg-white/5 hover:bg-white/10"
                                                        onClick={() => {
                                                            let url = log.result_url;
                                                            if (url?.startsWith('{')) {
                                                                try {
                                                                    const parsed = JSON.parse(url);
                                                                    url = parsed.resultUrls?.[0] || parsed.url || url;
                                                                } catch { }
                                                            }
                                                            window.open(url!, '_blank');
                                                        }}
                                                    >
                                                        <ExternalLink className="h-3.5 w-3.5" />
                                                        Source
                                                    </Button>
                                                )}
                                                {/* Manual refund button for failed jobs */}
                                                {log.status === 'failed' && log.refunded_at && (
                                                    <>
                                                        <Badge className="bg-green-500/10 text-green-500 border-green-500/20 text-[10px] h-5">
                                                            Refunded
                                                        </Badge>
                                                        {/* Recover from KIE button - check if video exists and re-deduct credits */}
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 px-3 text-xs gap-1.5 bg-cyan-500/10 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
                                                            onClick={() => handleRecoverRefundedJob(log)}
                                                            disabled={checkingStatus === log.id}
                                                        >
                                                            {checkingStatus === log.id ? (
                                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                            ) : (
                                                                <Zap className="h-3.5 w-3.5" />
                                                            )}
                                                            Recover KIE
                                                        </Button>
                                                    </>
                                                )}
                                                {log.status === 'failed' && !log.refunded_at && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 px-3 text-xs gap-1.5 bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20"
                                                        type="button"
                                                        onClick={(event) => openConfirm("refund", log, event)}
                                                        disabled={refundingLogId === log.id}
                                                    >
                                                        {refundingLogId === log.id ? (
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        ) : (
                                                            <RotateCcw className="h-3.5 w-3.5" />
                                                        )}
                                                        Refund {log.credits_used}
                                                    </Button>
                                                )}
                                                {/* KIE Logs button for failed jobs */}
                                                {log.status === 'failed' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 px-3 text-xs gap-1.5 bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
                                                        onClick={() => {
                                                            const searchParam = log.task_id || log.id;
                                                            navigate(`/admin?tab=kie-logs&search=${encodeURIComponent(searchParam)}`);
                                                        }}
                                                    >
                                                        <Activity className="h-3.5 w-3.5" />
                                                        KIE Logs
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination Controls */}
                {!loading && filteredLogs.length > ITEMS_PER_PAGE && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
                        <div className="text-sm text-muted-foreground">
                            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredLogs.length)} of {filteredLogs.length}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Prev
                            </Button>
                            <span className="px-3 py-1 bg-muted rounded text-sm">
                                {currentPage} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Credit Orders Section (only for single user) */}
                {userId && creditOrders.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-white/10">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <CreditCard className="h-5 w-5 text-green-500" />
                            Credit Purchase History
                        </h3>
                        <div className="space-y-2">
                            {creditOrders.map((order) => (
                                <div
                                    key={order.id}
                                    className={`flex items-center justify-between p-3 rounded-lg border ${order.status === 'completed'
                                        ? 'bg-green-500/10 border-green-500/20'
                                        : order.status === 'pending'
                                            ? 'bg-yellow-500/10 border-yellow-500/20'
                                            : 'bg-muted/30 border-border/50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Plus className="h-5 w-5 text-green-500" />
                                        <div>
                                            <p className="font-medium">
                                                {order.credits_amount + (order.bonus_credits || 0)} Credits
                                                {order.bonus_credits > 0 && (
                                                    <span className="text-xs text-green-500 ml-1">
                                                        (+{order.bonus_credits} bonus)
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                ฿{order.price_thb?.toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <Badge className={
                                            order.status === 'completed'
                                                ? 'bg-green-500/10 text-green-500 border-green-500/20'
                                                : order.status === 'pending'
                                                    ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                                                    : ''
                                        }>
                                            {order.status}
                                        </Badge>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {formatDate(order.completed_at || order.created_at)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
            <AlertDialog open={!!confirmState.action} onOpenChange={(open) => !open && closeConfirm()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirmState.action === "refund"
                                ? "Refund credits to user?"
                                : "Mark job as failed?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmState.action === "refund"
                                ? `This will add ${confirmState.log?.credits_used ?? 0} credits back to the user.`
                                : "This will mark the job as failed and attempt to refund credits."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmAction}>
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Credit History Popup */}
            <Dialog open={showCreditHistory} onOpenChange={setShowCreditHistory}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CreditCard className="h-5 w-5 text-green-500" />
                            ประวัติการเติมเครดิต
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Summary */}
                        <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                            <div className="text-center">
                                <p className="text-xs text-muted-foreground mb-1">ยอดเติมทั้งหมด</p>
                                <p className="text-2xl font-bold text-green-500">
                                    ฿{creditOrders
                                        .filter(o => o.status === 'completed')
                                        .reduce((sum, o) => sum + (o.price_thb || 0), 0)
                                        .toLocaleString()}
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="text-xs text-muted-foreground mb-1">เครดิตที่ได้รับ</p>
                                <p className="text-2xl font-bold text-primary">
                                    {creditOrders
                                        .filter(o => o.status === 'completed')
                                        .reduce((sum, o) => sum + (o.credits_amount || 0) + (o.bonus_credits || 0), 0)
                                        .toLocaleString()}
                                </p>
                            </div>
                        </div>

                        {/* Order Count */}
                        <div className="text-center text-sm text-muted-foreground">
                            {creditOrders.filter(o => o.status === 'completed').length} รายการ
                            {creditOrders.filter(o => o.status === 'pending').length > 0 && (
                                <span className="ml-2 text-yellow-500">
                                    ({creditOrders.filter(o => o.status === 'pending').length} รอดำเนินการ)
                                </span>
                            )}
                        </div>

                        {/* Order List */}
                        {creditOrders.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                ยังไม่มีประวัติการเติมเครดิต
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {creditOrders.map((order) => (
                                    <div
                                        key={order.id}
                                        className={`flex items-center justify-between p-3 rounded-lg border ${order.status === 'completed'
                                            ? 'bg-green-500/10 border-green-500/20'
                                            : order.status === 'pending'
                                                ? 'bg-yellow-500/10 border-yellow-500/20'
                                                : 'bg-muted/30 border-border/50'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Plus className="h-5 w-5 text-green-500" />
                                            <div>
                                                <p className="font-medium">
                                                    {order.credits_amount + (order.bonus_credits || 0)} Credits
                                                    {order.bonus_credits > 0 && (
                                                        <span className="text-xs text-green-500 ml-1">
                                                            (+{order.bonus_credits} bonus)
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    ฿{order.price_thb?.toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <Badge className={
                                                order.status === 'completed'
                                                    ? 'bg-green-500/10 text-green-500 border-green-500/20'
                                                    : order.status === 'pending'
                                                        ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                                                        : ''
                                            }>
                                                {order.status === 'completed' ? 'สำเร็จ' : order.status === 'pending' ? 'รอดำเนินการ' : order.status}
                                            </Badge>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {formatDate(order.completed_at || order.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Image Preview Popup */}
            <Dialog open={!!previewImageUrl} onOpenChange={(open) => !open && setPreviewImageUrl(null)}>
                <DialogContent className="max-w-4xl p-2 bg-black/90 border-white/10">
                    <div className="relative">
                        <img
                            src={previewImageUrl || ''}
                            alt="Preview"
                            className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default UserGenerationLogs;
